import React, { useState, useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import DetailModal from '../components/DetailModal';
import AddDataModal from '../components/AddDataModal';
import { escapeHtml } from '../utils/safeHtml';

// --- PAYLOAD DROP MODAL ---
const DropPayloadModal = ({ coords, allItems, isAdmin, onClose, showToast }) => {
    const [selectedId, setSelectedId] = useState('');
    const [radius, setRadius] = useState(50);

    const handleAnchor = async () => {
        if (!isAdmin || !selectedId) return;
        const item = allItems.find(i => i.id === selectedId);
        if (!item) return;
        
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', item._collection, item.id), {
                lat: coords.lat.toFixed(6),
                lng: coords.lng.toFixed(6),
                radius: parseInt(radius, 10) || 50
            });
            showToast("Payload Anchored to GPS.", "success");
            onClose();
        } catch(e) { 
            console.error(e);
            showToast("Error anchoring payload.", "error"); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[5000] p-4 modal-overlay fade-in">
            <div className="bg-[#0a0a0a] border border-cyan-900/50 p-8 w-full max-w-md rounded shadow-[0_0_50px_rgba(6,182,212,0.15)] flex flex-col gap-6 z-50">
                <div>
                    <h3 className="text-white font-black uppercase tracking-widest flex items-center gap-2 mb-2 text-lg">
                        <Icons.MapPin className="text-cyan-500"/> Anchor Payload
                    </h3>
                    <div className="text-xs text-cyan-500 font-mono bg-cyan-900/10 border border-cyan-900/30 inline-block px-3 py-1.5 rounded">
                        LAT: {coords.lat.toFixed(6)} {'//'} LNG: {coords.lng.toFixed(6)}
                    </div>
                </div>

                {isAdmin && (
                    <>
                        <div>
                            <label className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-2 block">Select Record to Anchor</label>
                            <select className="w-full bg-[#111] border border-gray-800 text-white p-3 text-xs outline-none focus:border-cyan-600" value={selectedId} onChange={e=>setSelectedId(e.target.value)}>
                                <option value="">-- SELECT DATA POINT --</option>
                                {allItems.map(n => <option key={n.id} value={n.id}>[{n._typeLabel.toUpperCase()}] {n.title || n.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-2 block">AR Trigger Radius (Meters)</label>
                            <input type="number" className="w-full bg-[#111] border border-gray-800 text-white p-3 text-xs outline-none focus:border-cyan-600 font-mono" value={radius} onChange={e=>setRadius(e.target.value)} />
                        </div>
                    </>
                )}
                
                <div className="flex gap-2 mt-2">
                    <button onClick={onClose} className="flex-1 p-3 text-gray-500 text-xs font-bold uppercase tracking-widest hover:text-white transition-colors border border-gray-800 rounded">CANCEL</button>
                    {isAdmin && <button onClick={handleAnchor} disabled={!selectedId} className="flex-[2] bg-cyan-900/40 border border-cyan-900/50 text-cyan-400 text-xs font-bold uppercase tracking-widest hover:bg-cyan-900 hover:text-white transition-colors disabled:opacity-30 rounded">LOCK VECTOR</button>}
                </div>
            </div>
        </div>
    );
};

// --- MAIN MAP VIEW ---
const MapView = ({ data, isAdmin, showToast }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const layerGroupRef = useRef(null);
    
    const [pendingCoords, setPendingCoords] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editing, setEditing] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // showToast is a new function on every hub render; keep it out of the pin effect's dependencies
    // so the pins and any open popup aren't torn down and rebuilt each time the hub re-renders.
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;

    const allMapItems = useMemo(() => {
        if (!data) return [];
        const arts = (data.artifacts || []).map(a => ({ ...a, _collection: 'artifacts', _color: '#f97316', _typeLabel: 'Artifact' }));
        const ideas = (data.ideas || []).map(i => ({ ...i, _collection: 'ideas', _color: '#6366f1', _typeLabel: 'Lore' }));
        const sites = (data.sites || []).map(s => ({ ...s, _collection: 'sites', _color: '#06b6d4', _typeLabel: 'Site' }));
        const journals = (data.journals || []).map(j => ({ ...j, _collection: 'journals', _color: '#eab308', _typeLabel: j.author + ' Log' }));
        return [...arts, ...ideas, ...sites, ...journals];
    }, [data]);

    const locationGroups = useMemo(() => {
        const groups = {};
        const itemsWithCoords = allMapItems.filter(item => item.lat && item.lng && item.lat !== "" && item.lng !== "");
        itemsWithCoords.forEach(node => {
            const lat = parseFloat(node.lat);
            const lng = parseFloat(node.lng);
            if (isNaN(lat) || isNaN(lng)) return;
            
            const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
            if (!groups[key]) groups[key] = { lat, lng, items: [], total: 0 };
            
            groups[key].items.push(node);
            groups[key].total += 1;
        });
        return Object.values(groups);
    }, [allMapItems]);

    // NEW: Clear the entire board
    const handleClearBoard = async () => {
        const anchored = allMapItems.filter(i => i.lat && i.lng && i.lat !== "" && i.lng !== "");
        if (anchored.length === 0) return showToast("The board is already empty.", "error");
        
        if (!window.confirm(`WARNING: This will remove ALL ${anchored.length} GPS anchors from the map. The items will remain in the database, but they will be stripped of their location data. Are you absolutely sure?`)) return;
        
        try {
            showToast("Clearing board...", "success");
            const promises = anchored.map(item => 
                updateDoc(doc(db, 'artifacts', appId, 'public', 'data', item._collection, item.id), { lat: '', lng: '', radius: '' })
            );
            await Promise.all(promises);
            showToast("Board cleared.", "success");
        } catch(e) {
            console.error(e);
            showToast("Error clearing board.", "error");
        }
    };

    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;
        
        const map = L.map(mapRef.current, { zoomControl: false }).setView([47.6062, -122.3321], 12);
        map.attributionControl.setPrefix(false);
        // The free CARTO tiles and OpenStreetMap data require this credit to stay visible.
        L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>'
        }).addTo(map);
        
        layerGroupRef.current = L.layerGroup().addTo(map);
        mapInstance.current = map;

        map.on('click', (e) => {
            if(isAdmin) setPendingCoords(e.latlng);
        });

        return () => {
            map.remove();
            mapInstance.current = null;
        };
    }, [isAdmin]);

    useEffect(() => {
        if (!mapInstance.current || !layerGroupRef.current) return;
        layerGroupRef.current.clearLayers();

        locationGroups.forEach(group => {
            const { lat, lng, total, items } = group;
            const primaryColor = items[0]._color;
            const radius = parseFloat(items[0].radius) || 50;

            L.circle([lat, lng], {
                color: primaryColor, weight: 1.5, fillColor: primaryColor, fillOpacity: 0.1, radius: radius
            }).addTo(layerGroupRef.current);

            let markerHtml = '';
            if (total > 1) {
                markerHtml = `<div style="background:rgba(10,10,10,0.9);color:#fff;border-radius:4px;padding:4px 8px;font-size:10px;font-weight:900;border:1px solid ${primaryColor};box-shadow:0 0 15px rgba(0,0,0,0.9); white-space:nowrap; backdrop-filter:blur(4px);">${total} ITEMS</div>`;
            } else {
                markerHtml = `<div style="width:14px;height:14px;background:${primaryColor};border-radius:50%;box-shadow:0 0 20px ${primaryColor}; border: 2px solid rgba(0,0,0,0.8);"></div>`;
            }
            
            const customIcon = L.divIcon({ className: 'custom-pin', html: markerHtml, iconSize: total > 1 ? [60,24] : [14,14], iconAnchor: total > 1 ? [30,12] : [7,7] });
            const marker = L.marker([lat, lng], { icon: customIcon }).addTo(layerGroupRef.current);
            
            const popupContent = document.createElement('div');
            popupContent.className = 'text-left font-sans p-2 min-w-[200px] text-white';
            
            let innerHtml = `<div style="font-weight:900;margin-bottom:8px;border-bottom:1px solid #333;padding-bottom:4px;text-transform:uppercase;letter-spacing:1px;color:#888;">NODE CLUSTER</div>`;
            innerHtml += `<div style="max-height:150px;overflow-y:auto;margin-bottom:8px;">`;
            
            // NEW: Added the "Unanchor" [X] button to the HTML injection
            items.forEach((item, idx) => {
                innerHtml += `<div style="padding:6px;margin-bottom:4px;background:rgba(0,0,0,0.5);border-left:3px solid ${item._color};font-size:11px;display:flex;justify-content:space-between;align-items:center;transition:background 0.2s;" onmouseover="this.style.background='rgba(255,255,255,0.1)'" onmouseout="this.style.background='rgba(0,0,0,0.5)'">
                    <span id="item-click-${idx}" style="cursor:pointer;flex:1;"><strong style="color:${item._color}">${escapeHtml(item._typeLabel.toUpperCase())}</strong>: ${escapeHtml(item.title || item.name)}</span>
                    ${isAdmin ? `<button id="unanchor-${idx}" style="color:#ef4444;padding:2px 6px;margin-left:8px;background:rgba(239,68,68,0.2);border-radius:2px;font-weight:bold;cursor:pointer;" title="Remove from Map">X</button>` : ''}
                </div>`;
            });
            innerHtml += `</div>`;
            
            const latSafe = lat.toString().replace('.', '_');
            const lngSafe = lng.toString().replace('.', '_');
            if (isAdmin) {
                innerHtml += `<button id="btn-add-${latSafe}-${lngSafe}" style="background:rgba(6,182,212,0.2);color:#06b6d4;border:1px solid rgba(6,182,212,0.5);padding:8px;font-size:10px;font-weight:900;cursor:pointer;width:100%;text-transform:uppercase;letter-spacing:1px;border-radius:2px;margin-top:4px;transition:all 0.2s;" onmouseover="this.style.background='#06b6d4';this.style.color='#000';" onmouseout="this.style.background='rgba(6,182,212,0.2)';this.style.color='#06b6d4';">+ ANCHOR PAYLOAD HERE</button>`;
            }

            popupContent.innerHTML = innerHtml;
            marker.bindPopup(popupContent);
            
            marker.on('popupopen', () => {
                if (isAdmin) {
                    const btn = document.getElementById(`btn-add-${latSafe}-${lngSafe}`);
                    if (btn) btn.onclick = () => { mapInstance.current.closePopup(); setPendingCoords({ lat, lng }); };
                }
                items.forEach((item, idx) => {
                    const itemBtn = document.getElementById(`item-click-${idx}`);
                    if (itemBtn) itemBtn.onclick = () => { mapInstance.current.closePopup(); setSelectedItem(item); };
                    
                    // NEW: Bind the Unanchor logic to the [X] button
                    if (isAdmin) {
                        const unanchorBtn = document.getElementById(`unanchor-${idx}`);
                        if (unanchorBtn) {
                            unanchorBtn.onclick = async (e) => {
                                e.stopPropagation();
                                mapInstance.current.closePopup();
                                if(window.confirm(`Remove GPS anchor for ${item.title || item.name}?`)) {
                                    try {
                                        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', item._collection, item.id), { lat: '', lng: '', radius: '' });
                                        showToastRef.current("Anchor removed.", "success");
                                    } catch(err) { showToastRef.current("Failed to remove anchor.", "error"); }
                                }
                            };
                        }
                    }
                });
            });
        });
        
        if (pendingCoords) {
            const targetHtml = `<div style="width:24px;height:24px;border-radius:50%;border:2px solid #ef4444;background:rgba(0,0,0,0.5);animation:targetPulse 1.5s infinite;"></div>`;
            const targetIcon = L.divIcon({ className: 'target-pin', html: targetHtml, iconSize: [24,24], iconAnchor:[12,12] });
            L.marker([pendingCoords.lat, pendingCoords.lng], { icon: targetIcon, zIndexOffset: 1000 }).addTo(layerGroupRef.current);
        }
    }, [locationGroups, pendingCoords, isAdmin]);

    const handleMapSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(searchQuery)}`);
            const searchData = await res.json();
            if (searchData && searchData.length > 0) {
                const lat = parseFloat(searchData[0].lat);
                const lon = parseFloat(searchData[0].lon);
                mapInstance.current.flyTo([lat, lon], 18, { animate: true, duration: 1.5 });
                
                if (isAdmin) setTimeout(() => { setPendingCoords({ lat: lat, lng: lon }); }, 1500);
            } else {
                showToast("Location not found.", "error");
            }
        } catch (err) { showToast("Search failed.", "error"); }
        setIsSearching(false);
    };

    // Drop where you are standing: reads this device's GPS and opens the same "lock vector" box as a map click.
    const handleUseMyLocation = () => {
        if (!navigator.geolocation) return showToast("This device has no GPS.", "error");
        showToast("Finding your location...", "success");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude, lng = pos.coords.longitude;
                mapInstance.current.flyTo([lat, lng], 19, { animate: true, duration: 1 });
                setTimeout(() => setPendingCoords({ lat, lng }), 1000);
                if (pos.coords.accuracy > 25) showToast(`GPS is only accurate to ${Math.round(pos.coords.accuracy)} m. Stand still outside for a moment and try again for a tighter spot.`, "error");
            },
            () => showToast("Could not get your location. Allow location access and try again.", "error"),
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
    };

    const handleSaveMapItem = async (formData) => {
        try {
            if (!editing || !editing.id) return;
            const collName = editing._collection || 'artifacts';
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', collName, editing.id), formData);
            showToast("Record updated.", "success");
            setEditing(null);
        } catch (err) { showToast("Save failed.", "error"); }
    };

    return (
        <div className="flex-1 relative flex flex-col h-full bg-[#050505] fade-in overflow-hidden">
            
            <style>{`
                .leaflet-container { 
                    background: #050505 !important; 
                    font-family: 'Inter', sans-serif; 
                    z-index: 10; 
                    cursor: ${isAdmin ? 'crosshair !important' : 'grab'};
                }
                .leaflet-container:active { cursor: ${isAdmin ? 'crosshair !important' : 'grabbing'}; }
                .leaflet-popup-pane { z-index: 1000 !important; }
                .leaflet-popup-content-wrapper {
                    background: rgba(10, 10, 10, 0.95) !important;
                    border: 1px solid #333 !important;
                    color: #fff !important;
                    border-radius: 4px !important;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8) !important;
                    backdrop-filter: blur(4px);
                }
                .leaflet-popup-tip { background: rgba(10, 10, 10, 0.95) !important; border: 1px solid #333 !important; }
                .leaflet-popup-content p { margin: 0 !important; }
                .leaflet-control-attribution { background: rgba(10, 10, 10, 0.7) !important; color: #777 !important; font-size: 9px; }
                .leaflet-control-attribution a { color: #999 !important; }
                .leaflet-popup-close-button { color: #fff !important; text-shadow: none !important; }
                .leaflet-popup-close-button:hover { color: #f97316 !important; }
                @keyframes targetPulse {
                    0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                    70% { transform: scale(1.2); box-shadow: 0 0 0 15px rgba(239, 68, 68, 0); }
                    100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }
            `}</style>

            <div className="absolute inset-0 pointer-events-none z-[300] shadow-[inset_0_0_150px_rgba(0,0,0,0.9)]"></div>

            <div className="absolute top-6 left-6 z-[400] pointer-events-none">
                <h2 className="text-xl font-black flex items-center gap-3 text-white tracking-tight uppercase drop-shadow-md">
                    <Icons.MapPin className="text-cyan-500" /> GEOSPATIAL TRACKING
                </h2>
                <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest mt-1 drop-shadow-md bg-black/60 backdrop-blur-md inline-block px-3 py-1.5 rounded border border-gray-800/50">
                    {isAdmin ? "Admin: Map & Anchor Utility" : "Live AR Trigger Matrix"}
                </p>
            </div>
            
            <div className="absolute top-6 right-6 z-[400] flex flex-col items-end gap-3 w-full max-w-sm pointer-events-none">
                <div className="flex gap-2">
                    <div className="flex items-center gap-2 bg-black/60 border border-gray-800/50 px-3 py-1.5 rounded shadow-xl backdrop-blur-md">
                        <div className="w-2 h-2 rounded-full bg-[#f97316] shadow-[0_0_8px_#f97316]"></div><span className="text-[9px] font-bold text-gray-300 uppercase">Artifact</span>
                    </div>
                    <div className="flex items-center gap-2 bg-black/60 border border-gray-800/50 px-3 py-1.5 rounded shadow-xl backdrop-blur-md">
                        <div className="w-2 h-2 rounded-full bg-[#6366f1] shadow-[0_0_8px_#6366f1]"></div><span className="text-[9px] font-bold text-gray-300 uppercase">Lore</span>
                    </div>
                    <div className="flex items-center gap-2 bg-black/60 border border-gray-800/50 px-3 py-1.5 rounded shadow-xl backdrop-blur-md">
                        <div className="w-2 h-2 rounded-full bg-[#eab308] shadow-[0_0_8px_#eab308]"></div><span className="text-[9px] font-bold text-gray-300 uppercase">Journal</span>
                    </div>
                </div>

                {isAdmin && (
                    <div className="flex gap-2 w-full mt-1">
                        <button onClick={handleClearBoard} className="bg-red-900/40 border border-red-900/50 text-red-400 px-3 rounded hover:bg-red-900 hover:text-white transition-colors flex items-center justify-center pointer-events-auto shadow-2xl" title="Clear All Anchors">
                            <Icons.Trash2 size={14} />
                        </button>
                        <button onClick={handleUseMyLocation} className="bg-cyan-900/40 border border-cyan-900/50 text-cyan-400 px-3 rounded hover:bg-cyan-900 hover:text-white transition-colors flex items-center justify-center pointer-events-auto shadow-2xl" title="Drop here: use this device's GPS">
                            <Icons.MapPin size={14} />
                        </button>
                        <form onSubmit={handleMapSearch} className="flex flex-1 shadow-2xl pointer-events-auto bg-black/80 backdrop-blur-md p-1.5 border border-gray-800 rounded">
                            <input type="text" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search address..." className="flex-1 bg-transparent text-white p-2 text-xs outline-none focus:border-cyan-500 font-mono placeholder-gray-600" />
                            <button type="submit" disabled={isSearching} className="bg-cyan-900/40 border border-cyan-900/50 text-cyan-400 px-4 rounded hover:bg-cyan-900 hover:text-white transition-colors flex items-center justify-center disabled:opacity-50">
                                {isSearching ? <Icons.Activity size={16} className="animate-spin" /> : <Icons.Search size={16} />}
                            </button>
                        </form>
                    </div>
                )}
            </div>

            <div ref={mapRef} className="w-full h-full z-10 bg-[#050505]"></div>

            {pendingCoords && (
                <DropPayloadModal coords={pendingCoords} allItems={allMapItems} isAdmin={isAdmin} onClose={() => setPendingCoords(null)} showToast={showToast} />
            )}

            {selectedItem && (
                <DetailModal item={selectedItem} type={selectedItem._typeLabel.toUpperCase()} isAdmin={isAdmin} onClose={() => setSelectedItem(null)} onEdit={(item) => { setSelectedItem(null); setEditing(item); }} />
            )}

            {editing && (
                <AddDataModal type={editing._collection === 'artifacts' ? 'artifact' : editing._collection === 'ideas' ? 'idea' : editing._collection === 'journals' ? 'journal_entry' : 'site'} initialData={editing} onClose={() => setEditing(null)} onSubmit={handleSaveMapItem} showToast={showToast} isAdmin={isAdmin} />
            )}
        </div>
    );
};

export default MapView;